import { Account, AccountPair, Bundle, BundlePortfolio, ClientAccount } from '@lune-climate/lune'
import { useErrorSnackbar } from '@lune-fe/lune-components-lib'
import { ButtonNew, Text } from '@lune-fe/lune-ui-lib'
import Check from '@mui/icons-material/Check'
import CreateIcon from '@mui/icons-material/CreateOutlined'
import { Box } from '@mui/material'
import { Big } from 'big.js'
import { FORM_ERROR } from 'final-form'
import { FC, ReactElement, useCallback, useMemo, useState } from 'react'

import AccountForm from 'components/AccountForm/AccountForm'
import ConditionalTooltip from 'components/ConditionalTooltip'
import useMixpanel from 'hooks/useMixpanel'
import { calculateBundleSelectionTotalSum } from 'utils'
import BundlePicker, {
    BundlePickerView,
    CUSTOM_PORTFOLIO_ID,
} from 'views/Settings/BundlePicker/BundlePicker'

import WhiteBoxContainer from '../WhiteBoxContainer'

export enum AccountFormKeys {
    NAME = 'name',
    CURRENCY = 'currency',
    BENEFICIARY = 'beneficiary',
    HANDLE = 'handle',
    EXTERNAL_PAGES_VISIBILITY = 'externalPagesVisibility',
    BUNDLE_SETTINGS = 'bundleSettings',
}

export interface BundleSettings {
    customBundleSelection: Record<string, number | string>
    selectedBundlePortfolioId: string
}

export interface IAccountFormData {
    name?: string
    currency: string
    beneficiary?: string
    handle?: string
    externalPagesVisibility?: string
    bundleSettings: BundleSettings
}

export enum AccountFormType {
    CLIENT_ACCOUNT = 'client-account',
    ACCOUNT = 'account',
}

const AccountFormWrapper = ({
    onSubmit,
    initialValues,
    onSuccess,
    readOnly,
    existingAccount,
    disabledFields,
    bundles,
    forbiddenTooltip,
    onFormValueChange,
    bundlesPortfolios,
    formType,
    account,
    onLogoUpdate,
    logoUpdating,
}: {
    onSubmit: (data: IAccountFormData) => Promise<Account | AccountPair | ClientAccount | undefined>
    initialValues: IAccountFormData
    onSuccess: (res?: any) => void
    readOnly?: boolean
    existingAccount?: boolean
    disabledFields?: AccountFormKeys[]
    bundles?: Bundle[]
    forbiddenTooltip: FC<{ children: ReactElement; contentWidth?: boolean }>
    onFormValueChange?: (
        formValue: IAccountFormData,
        bundleSettings: BundleSettings,
        bundlePickerError: boolean,
    ) => void
    bundlesPortfolios: BundlePortfolio[]
    formType: AccountFormType
    account?: ClientAccount | Account
    onLogoUpdate?: (file: File | null) => void
    logoUpdating?: boolean
}) => {
    const [view, setView] = useState(BundlePickerView.PERCENTAGES)
    const [formValue, setFormValue] = useState<IAccountFormData>()
    const [bundleSettings, setBundleSettings] = useState<BundleSettings>({
        customBundleSelection: initialValues[AccountFormKeys.BUNDLE_SETTINGS].customBundleSelection,
        selectedBundlePortfolioId:
            initialValues[AccountFormKeys.BUNDLE_SETTINGS].selectedBundlePortfolioId,
    })
    const [bundlePickerError, setBundlePickerError] = useState<boolean>(false)
    const mixpanel = useMixpanel()
    const { enqueueSnackbar: snackbar } = useErrorSnackbar()

    const onSubmitForm = async (values?: IAccountFormData) => {
        if (values) {
            try {
                const beneficiary = values.beneficiary?.trim()
                const res = await onSubmit({
                    name: values.name!.trim(),
                    beneficiary,
                    currency: values.currency,
                    handle: values.handle,
                    externalPagesVisibility: values.externalPagesVisibility,
                    bundleSettings: {
                        customBundleSelection: bundleSettings.customBundleSelection,
                        selectedBundlePortfolioId: bundleSettings.selectedBundlePortfolioId,
                    },
                })

                if (!res) {
                    throw new Error('Empty response means something went wrong')
                }

                if (
                    bundleSettings.customBundleSelection !==
                        initialValues[AccountFormKeys.BUNDLE_SETTINGS].customBundleSelection ||
                    bundleSettings.selectedBundlePortfolioId !==
                        initialValues[AccountFormKeys.BUNDLE_SETTINGS].selectedBundlePortfolioId
                ) {
                    mixpanel.track('settings_default_bundles_saved')
                }

                if (beneficiary) {
                    mixpanel.track('settings_beneficiary_saved')
                }

                onSuccess(res)
            } catch (error) {
                const msg = `Something went wrong. Please contact support if this happens again.`
                snackbar(msg, { error })
                return {
                    [FORM_ERROR]: msg,
                }
            }
        }
    }

    const onChangeCustomBundleSelection = useCallback(
        (selection: Record<string, string | number>) => {
            const error = !calculateBundleSelectionTotalSum(selection).eq(Big(100))
            setBundlePickerError(error)
            setBundleSettings((prev) => {
                return {
                    ...prev,
                    customBundleSelection: selection,
                }
            })
            if (formValue) {
                onFormValueChange?.(
                    formValue,
                    {
                        customBundleSelection: selection,
                        selectedBundlePortfolioId: bundleSettings.selectedBundlePortfolioId,
                    },
                    error,
                )
            }
        },
        [formValue],
    )

    const onChangeBundlePortfolio = useCallback(
        (selection: string) => {
            setBundleSettings((prev) => {
                const updated = {
                    ...prev,
                    selectedBundlePortfolioId: selection,
                }
                if (formValue) {
                    onFormValueChange?.(formValue, updated, bundlePickerError)
                }
                return updated
            })
        },
        [formValue, bundlePickerError],
    )

    const bundlesAlphabetical = useMemo(() => {
        if (!bundles) {
            return []
        }
        return bundles.sort((a, b) => a.name.localeCompare(b.name))
    }, [bundles])

    return (
        <>
            <AccountForm
                account={account}
                formType={formType}
                existingAccount={existingAccount}
                disabledFields={disabledFields}
                readOnly={readOnly}
                initialValues={initialValues}
                setFormValue={(value) => {
                    const newValue = value instanceof Function ? value(formValue) : value
                    setFormValue(newValue)
                    if (newValue) {
                        onFormValueChange?.(newValue, bundleSettings, bundlePickerError)
                    }
                }}
                onSubmitForm={onSubmitForm}
                forbiddenTooltip={forbiddenTooltip}
                onLogoUpdate={onLogoUpdate}
                logoUpdating={logoUpdating}
            />
            <WhiteBoxContainer
                sx={{
                    p: 3,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 3,
                    }}
                >
                    <Text variant={'body2-medium'}>Default bundles</Text>
                    {view === BundlePickerView.PERCENTAGES && (
                        <>
                            {bundleSettings.selectedBundlePortfolioId === CUSTOM_PORTFOLIO_ID && (
                                <ConditionalTooltip show={!!readOnly} tooltip={forbiddenTooltip}>
                                    <ButtonNew
                                        size={'sm'}
                                        disabled={readOnly}
                                        data-testid="account-edit-bundles-btn"
                                        style={`outline`}
                                        leadingIcon={<CreateIcon />}
                                        onClick={() => setView(BundlePickerView.SWITCHES)}
                                    >
                                        Edit bundles
                                    </ButtonNew>
                                </ConditionalTooltip>
                            )}
                        </>
                    )}
                    {view === BundlePickerView.SWITCHES && (
                        <ButtonNew
                            size={'sm'}
                            data-testid="account-save-bundles-btn"
                            leadingIcon={<Check />}
                            style={`outline`}
                            onClick={() => setView(BundlePickerView.PERCENTAGES)}
                            disabled={!Object.keys(bundleSettings.customBundleSelection).length}
                        >
                            Save bundles
                        </ButtonNew>
                    )}
                </Box>
                <BundlePicker
                    view={view}
                    readOnly={readOnly}
                    customBundleSelection={bundleSettings.customBundleSelection}
                    onChangeCustomBundleSelection={onChangeCustomBundleSelection}
                    selectedBundlePortfolioId={bundleSettings.selectedBundlePortfolioId}
                    onChangeBundlePortfolio={onChangeBundlePortfolio}
                    allBundles={bundlesAlphabetical}
                    predefinedBundlePortfolios={bundlesPortfolios}
                />
            </WhiteBoxContainer>
        </>
    )
}

export default AccountFormWrapper
